import { BookOpen, Pencil, Plus, Star, Trash2, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import AdminLayout from '../../components/admin/AdminLayout';
import { ResponsiveTable } from '../../components/admin/responsive';
import { api } from '../../services/api';

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

// Comprime imagem para max 350px de largura, JPEG 0.78 — resultado ~20-50KB
function comprimirImagem(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 350;
        let { width, height } = img;
        if (width > MAX) {
          height = Math.round((height * MAX) / width);
          width = MAX;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const base64 = canvas.toDataURL('image/jpeg', 0.78);
        resolve(base64);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function mesValorParaStr(date) {
  const d = new Date(date);
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${d.getUTCFullYear()}-${m}`;
}

const FORM_VAZIO = {
  title: '',
  author: '',
  month: '',
  synopsis: '',
  coverBase64: '',
};

export default function Livros() {
  const [livros, setLivros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null); // null = novo
  const [form, setForm] = useState(FORM_VAZIO);
  const [preview, setPreview] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const inputFileRef = useRef();

  async function carregar() {
    setCarregando(true);
    try {
      const { data } = await api.get('/admin/livros');
      setLivros(data);
    } catch {
      setErro('Erro ao carregar livros.');
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  function abrirNovo() {
    const hoje = new Date();
    const m = String(hoje.getMonth() + 1).padStart(2, '0');
    setEditando(null);
    setForm({ ...FORM_VAZIO, month: `${hoje.getFullYear()}-${m}` });
    setPreview('');
    setErro('');
    setModalAberto(true);
  }

  function abrirEditar(livro) {
    setEditando(livro.id);
    setForm({
      title: livro.title,
      author: livro.author,
      month: mesValorParaStr(livro.month),
      synopsis: livro.synopsis || '',
      coverBase64: livro.coverBase64 || '',
    });
    setPreview(livro.coverBase64 || '');
    setErro('');
    setModalAberto(true);
  }

  function fecharModal() {
    setModalAberto(false);
    setEditando(null);
    setForm(FORM_VAZIO);
    setPreview('');
    setErro('');
  }

  async function handleImagem(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErro('Selecione uma imagem.');
      return;
    }
    try {
      const base64 = await comprimirImagem(file);
      setForm((f) => ({ ...f, coverBase64: base64 }));
      setPreview(base64);
      setErro('');
    } catch {
      setErro('Falha ao processar imagem.');
    }
  }

  async function salvar(e) {
    e.preventDefault();
    setErro('');
    if (!form.title || !form.author || !form.month) {
      setErro('Título, autor e mês são obrigatórios.');
      return;
    }
    setSalvando(true);
    try {
      // month-input retorna "2026-07", precisamos "2026-07-01" (ISO date)
      const monthDate = `${form.month}-01`;
      const payload = { ...form, month: monthDate };

      if (editando) {
        const { data } = await api.patch(`/admin/livros/${editando}`, payload);
        setLivros((ls) => ls.map((l) => (l.id === editando ? data : l)));
      } else {
        const { data } = await api.post('/admin/livros', payload);
        setLivros((ls) => [data, ...ls]);
      }
      fecharModal();
    } catch (err) {
      setErro(err?.response?.data?.erro || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  }

  async function deletar(id) {
    try {
      await api.delete(`/admin/livros/${id}`);
      setLivros((ls) => ls.filter((l) => l.id !== id));
    } catch {
      setErro('Erro ao deletar livro.');
    } finally {
      setConfirmDelete(null);
    }
  }

  async function toggleAtivo(livro) {
    try {
      const { data } = await api.patch(`/admin/livros/${livro.id}`, {
        active: !livro.active,
      });
      setLivros((ls) => ls.map((l) => (l.id === livro.id ? data : l)));
    } catch {
      setErro('Erro ao alterar status.');
    }
  }

  async function toggleDestaque(livro) {
    try {
      const novoValor = !livro.featured;
      const { data } = await api.patch(`/admin/livros/${livro.id}`, {
        featured: novoValor,
      });
      // Backend garante só 1 destaque — atualiza todos localmente
      setLivros((ls) =>
        ls.map((l) => {
          if (l.id === livro.id) return data;
          if (novoValor) return { ...l, featured: false }; // desmarca os outros
          return l;
        }),
      );
    } catch {
      setErro('Erro ao definir destaque.');
    }
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-pink-50 rounded-xl flex items-center justify-center">
              <BookOpen size={18} className="text-pink-500" />
            </div>
            <div>
              <h1 className="text-lg font-black text-slate-900">
                Livros Lidos
              </h1>
              <p className="text-xs text-slate-400">
                Arquivo de leituras do clube
              </p>
            </div>
          </div>
          <button
            onClick={abrirNovo}
            className="flex items-center gap-2 bg-[#ff2d6c] text-white text-xs font-black uppercase tracking-wide px-4 py-2.5 rounded-full hover:bg-[#e02059] transition-all shadow-lg shadow-pink-500/20"
          >
            <Plus size={14} /> Adicionar livro
          </button>
        </div>

        {erro && !modalAberto && (
          <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-xs text-red-600">
            {erro}
          </div>
        )}

        {/* Lista */}
        {carregando ? (
          <div className="text-center text-slate-400 text-xs py-16 animate-pulse">
            Carregando livros...
          </div>
        ) : livros.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
            <BookOpen size={32} className="text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400 text-sm font-medium">
              Nenhum livro cadastrado ainda.
            </p>
            <button
              onClick={abrirNovo}
              className="mt-4 text-xs text-[#ff2d6c] font-black hover:underline"
            >
              + Adicionar primeiro livro
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {livros.map((livro) => (
              <div
                key={livro.id}
                className={`group relative bg-white rounded-2xl border shadow-sm overflow-hidden transition-all hover:shadow-md ${
                  livro.active
                    ? 'border-slate-100'
                    : 'border-dashed border-slate-200 opacity-60'
                }`}
              >
                {/* Capa */}
                <div className="aspect-[3/4] bg-gradient-to-br from-pink-50 to-slate-50 relative overflow-hidden">
                  {livro.coverBase64 ? (
                    <img
                      src={livro.coverBase64}
                      alt={livro.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen size={28} className="text-slate-200" />
                    </div>
                  )}

                  {/* Badge mês */}
                  <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-md">
                    <span className="text-[8px] font-black uppercase text-[#ff2d6c] leading-none">
                      {livro.mesLabel}
                    </span>
                  </div>

                  {/* Badge destaque */}
                  {livro.featured && (
                    <div
                      className="absolute top-2 right-2 w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center shadow-md"
                      title="Leitura em destaque"
                    >
                      <Star size={11} className="text-white fill-white" />
                    </div>
                  )}

                  {/* Ações hover */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => toggleDestaque(livro)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${livro.featured ? 'bg-amber-400 hover:bg-amber-500' : 'bg-white hover:bg-amber-50'}`}
                      title={
                        livro.featured
                          ? 'Remover destaque'
                          : 'Definir como destaque'
                      }
                    >
                      <Star
                        size={13}
                        className={
                          livro.featured
                            ? 'text-white fill-white'
                            : 'text-amber-500'
                        }
                      />
                    </button>
                    <button
                      onClick={() => abrirEditar(livro)}
                      className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-pink-50 transition-colors"
                      title="Editar"
                    >
                      <Pencil size={13} className="text-slate-700" />
                    </button>
                    <button
                      onClick={() => setConfirmDelete(livro.id)}
                      className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-red-50 transition-colors"
                      title="Deletar"
                    >
                      <Trash2 size={13} className="text-red-500" />
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div className="p-2.5">
                  <h4 className="text-[10px] font-bold text-slate-800 uppercase tracking-tight line-clamp-2 leading-tight">
                    {livro.title}
                  </h4>
                  <p className="text-[9px] text-slate-400 italic font-serif mt-0.5 line-clamp-1">
                    {livro.author}
                  </p>
                  <button
                    onClick={() => toggleAtivo(livro)}
                    className={`mt-1.5 text-[8px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full transition-colors ${
                      livro.active
                        ? 'bg-green-50 text-green-600 border border-green-100'
                        : 'bg-slate-100 text-slate-400 border border-slate-200'
                    }`}
                  >
                    {livro.active ? 'Visível' : 'Oculto'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {modalAberto && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              width: '100%',
              maxWidth: '540px',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '24px',
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <h2
                style={{
                  fontSize: 'clamp(14px, 4vw, 18px)',
                  fontWeight: 900,
                  color: '#0f172a',
                  margin: 0,
                  textTransform: 'uppercase',
                }}
              >
                {editando ? 'Editar livro' : 'Novo livro'}
              </h2>
              <button
                onClick={fecharModal}
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.target.style.background = '#f1f5f9')}
                onMouseLeave={(e) =>
                  (e.target.style.background = 'transparent')
                }
              >
                <X size={16} style={{ color: '#64748b' }} />
              </button>
            </div>

            <form
              onSubmit={salvar}
              style={{
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              {/* Upload de imagem */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                  Capa do livro
                </label>
                <div
                  onClick={() => inputFileRef.current?.click()}
                  className="relative aspect-[3/4] max-w-[160px] mx-auto rounded-2xl overflow-hidden border-2 border-dashed border-slate-200 bg-slate-50 cursor-pointer hover:border-[#ff2d6c] hover:bg-pink-50/30 transition-all group"
                >
                  {preview ? (
                    <>
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Upload size={20} className="text-white" />
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-400">
                      <Upload size={24} />
                      <span className="text-[10px] font-medium text-center px-4">
                        Clique para enviar
                      </span>
                      <span className="text-[9px] text-slate-300">
                        JPG, PNG, WEBP
                      </span>
                    </div>
                  )}
                </div>
                <input
                  ref={inputFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImagem}
                />
                <p className="text-[9px] text-slate-300 text-center mt-1">
                  Imagem comprimida automaticamente para ~350px
                </p>
              </div>

              {/* Título */}
              <div>
                <label
                  style={{
                    fontSize: '10px',
                    fontWeight: 900,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                >
                  Título
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, title: e.target.value }))
                  }
                  required
                  placeholder="Ex: Quarta Asa"
                  style={{
                    width: '100%',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    minHeight: '44px',
                    boxSizing: 'border-box',
                    color: '#0f172a',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#ff2d6c')}
                  onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                />
              </div>

              {/* Autor */}
              <div>
                <label
                  style={{
                    fontSize: '10px',
                    fontWeight: 900,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                >
                  Autor(a)
                </label>
                <input
                  type="text"
                  value={form.author}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, author: e.target.value }))
                  }
                  required
                  placeholder="Ex: Rebecca Yarros"
                  style={{
                    width: '100%',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    minHeight: '44px',
                    boxSizing: 'border-box',
                    color: '#0f172a',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#ff2d6c')}
                  onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                />
              </div>

              {/* Mês */}
              <div>
                <label
                  style={{
                    fontSize: '10px',
                    fontWeight: 900,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                >
                  Mês de leitura
                </label>
                <input
                  type="month"
                  value={form.month}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, month: e.target.value }))
                  }
                  required
                  style={{
                    width: '100%',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    minHeight: '44px',
                    boxSizing: 'border-box',
                    color: '#0f172a',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#ff2d6c')}
                  onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                />
              </div>

              {/* Sinopse */}
              <div>
                <label
                  style={{
                    fontSize: '10px',
                    fontWeight: 900,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                >
                  Sinopse{' '}
                  <span
                    style={{
                      color: '#cbd5e1',
                      fontWeight: 400,
                      textTransform: 'none',
                    }}
                  >
                    (opcional)
                  </span>
                </label>
                <textarea
                  value={form.synopsis}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, synopsis: e.target.value }))
                  }
                  placeholder="Breve descrição do livro..."
                  rows={3}
                  style={{
                    width: '100%',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    fontSize: '16px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box',
                    color: '#0f172a',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    minHeight: '100px',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#ff2d6c')}
                  onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                />
              </div>

              {erro && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#dc2626',
                    background: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    padding: '12px',
                  }}
                >
                  {erro}
                </div>
              )}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    window.innerWidth < 480 ? '1fr' : '1fr 1fr',
                  gap: '12px',
                  marginTop: '16px',
                }}
              >
                <button
                  type="button"
                  onClick={fecharModal}
                  style={{
                    border: '1px solid #e2e8f0',
                    color: '#64748b',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    background: '#fff',
                    minHeight: '44px',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.target.style.background = '#f8fafc')}
                  onMouseLeave={(e) => (e.target.style.background = '#fff')}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvando}
                  style={{
                    background: '#ff2d6c',
                    color: '#fff',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    border: 'none',
                    cursor: salvando ? 'not-allowed' : 'pointer',
                    opacity: salvando ? 0.5 : 1,
                    minHeight: '44px',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) =>
                    !salvando && (e.target.style.background = '#e02059')
                  }
                  onMouseLeave={(e) => (e.target.style.background = '#ff2d6c')}
                >
                  {salvando
                    ? 'Salvando...'
                    : editando
                      ? 'Salvar alterações'
                      : 'Adicionar livro'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-xs w-full text-center space-y-4">
            <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mx-auto">
              <Trash2 size={20} className="text-red-500" />
            </div>
            <p className="text-sm font-bold text-slate-900">
              Deletar este livro?
            </p>
            <p className="text-xs text-slate-400">
              Esta ação não pode ser desfeita.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 border border-slate-200 text-slate-500 py-2.5 rounded-full text-xs font-black"
              >
                Cancelar
              </button>
              <button
                onClick={() => deletar(confirmDelete)}
                className="flex-1 bg-red-500 text-white py-2.5 rounded-full text-xs font-black hover:bg-red-600 transition-colors"
              >
                Deletar
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
